/**
 * Make screen reader speak
 * @param {string} label aria-label value
 * @param {object} element DOM element
 * */
export const screenReaderSpeak = (label) => {
  const element = document.createElement('div');
  let id = `speak-${new Date().getTime()}`;
  element.setAttribute('id', id);
  element.setAttribute('aria-live', 'polite');
  element.classList.add('screen-reader-only');
  window.document.body.appendChild(element);

  window.setTimeout(() => {
    element.innerHTML = label;
  }, 100);

  window.setTimeout(() => {
    document.body.removeChild(document.getElementById(id));
  }, 1000);
};

const DoNothing = () => {};

export const keys = {
  space: 32,
  enter: 13,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  home: 36,
  end: 35,
};

export const keyNames = {
  arrowUp: 'ArrowUp',
  arrowLeft: 'ArrowLeft',
  tab: 'Tab',
  arrowRight: 'ArrowRight',
  arrowDown: 'ArrowDown',
  home: 'Home',
  end: 'End',
};

export const handleArrowMovement =
  ({
    up = DoNothing,
    down = DoNothing,
    left = DoNothing,
    right = DoNothing,
    home = DoNothing,
    end = DoNothing,
  }) =>
  (event, ...args) => {
    let key = event.keyCode;
    switch (key) {
      case keys.left:
        left(event, ...args);
        break;
      case keys.up:
        up(event, ...args);
        break;
      case keys.right:
        right(event, ...args);
        break;
      case keys.down:
        down(event, ...args);
        break;
      case keys.home:
        home(event, ...args);
        break;
      case keys.end:
        end(event, ...args);
        break;
      default:
        break;
    }
  };

export const handleSpaceAndEnter =
  ({ space = DoNothing, enter = DoNothing }) =>
  (event, ...args) => {
    let key = event.keyCode;
    switch (key) {
      case keys.space:
        space(event, ...args);
        break;
      case keys.enter:
        enter(event, ...args);
        break;
      default:
        break;
    }
  };

export const handleTabFocus =
  ({ tab = DoNothing }) =>
  (event, ...args) => {
    let { key } = event;
    switch (key) {
      case keyNames.tab:
        tab(event, ...args);
        break;
      default:
        break;
    }
  };

const COLUMN_NAME_MAP = {
  starts_at: 'Start Date',
  due_at: 'Due Date',
  title: 'Title',
  skills: 'Skills',
  // don't forget to add your other columns here,
  // otherwise you'll always get "Unsorted."
};

const getSortOrder = (selection) => {
  if (!selection) return;
  const a = selection.split('_');
  return a[a.length - 1];
};

const getColumnName = (selection) =>
  !!selection && selection.split('_').slice(0, -1).join('_');

export const makeTableAriaLabel = ({ tableName, sortQuery }) => {
  const TABLE_LABEL = `${tableName} table, sortable`;
  const UNSORTED_LABEL = 'Unsorted.';
  const DEFAULT_LABEL = `${TABLE_LABEL} ${UNSORTED_LABEL}`;

  if (!sortQuery) return DEFAULT_LABEL;

  const column = getColumnName(sortQuery);
  const order = getSortOrder(sortQuery);
  const columnName = COLUMN_NAME_MAP[column];
  if (order !== 'unsorted' && columnName) {
    return `${TABLE_LABEL}. Sorted by ${columnName}, ${order}.`;
  }
  return DEFAULT_LABEL;
};

export const tabsArrowHandler = handleArrowMovement({
  left: (evt) => {
    const el = evt.currentTarget;
    if (evt.currentTarget.previousSibling) {
      el.previousSibling.focus();
    } else {
      // go to end
      el.parentNode.lastChild.focus();
    }
  },
  right: (evt) => {
    const el = evt.currentTarget;
    if (evt.currentTarget.nextSibling) {
      el.nextSibling.focus();
    } else {
      // go to end
      el.parentNode.firstChild.focus();
    }
  },
  home: (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    evt.currentTarget.parentNode.firstChild.focus();
  },
  end: (evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    evt.currentTarget.parentNode.lastChild.focus();
  },
});

export const tabsTabKeyHandler = handleTabFocus({
  tab: (evt) => {
    const el = evt.currentTarget;
    let nextFocusEl;

    if (!evt.shiftKey) {
      if (el.nextSibling) {
        nextFocusEl = el.nextSibling;
      } else {
        keepActiveTabAsTabbable(evt);
        return;
      }
    } else if (evt.shiftKey) {
      if (el.previousSibling) {
        nextFocusEl = el.previousSibling;
      } else {
        keepActiveTabAsTabbable(evt);
        return;
      }
    }

    evt.preventDefault();
    el.setAttribute('tabindex', '-1');
    if (nextFocusEl) {
      nextFocusEl.setAttribute('tabindex', '0');
      nextFocusEl.focus();
    }
  },
});

const keepActiveTabAsTabbable = (evt) => {
  evt.currentTarget.parentNode.childNodes.forEach((tab) => {
    tab.setAttribute(
      'tabindex',
      tab.getAttribute('aria-selected') === 'true' ? '0' : '-1',
    );
  });
};

export const treeArrowHandler = handleArrowMovement({
  left: (evt, { parentId }) => {
    evt.stopPropagation();

    // should only happen on child nodes
    if (!parentId) return;

    const element = document.querySelector(`div[data-label-id='${parentId}']`);
    if (element) {
      element.focus();
    }
  },
  right: (evt, { index, parentId }) => {
    evt.stopPropagation();
    // should only happen on a parent node
    if (parentId) return;

    const element = document.querySelector(
      `div[data-accessibility-index='${index + 1}']`,
    );
    if (element) {
      element.focus();
    }
  },
  up: (evt, { index }) => {
    evt.stopPropagation();

    const element = document.querySelector(
      `div[data-accessibility-index='${index - 1}']`,
    );
    if (element) {
      element.focus();
    }
  },
  down: (evt, { index }) => {
    evt.stopPropagation();

    const element = document.querySelector(
      `div[data-accessibility-index='${index + 1}']`,
    );
    if (element) {
      element.focus();
    }
  },
  home: (evt) => {
    const element = document.querySelector(`div[data-accessibility-index='0']`);
    if (element) {
      evt.preventDefault();
      evt.stopPropagation();
      element.focus();
    }
  },
  end: (evt, { lastIndex }) => {
    const element = document.querySelector(
      `div[data-accessibility-index='${lastIndex}']`,
    );
    if (element) {
      evt.preventDefault();
      evt.stopPropagation();
      element.focus();
    }
  },
});

export const spaceAndEnterHandler = handleSpaceAndEnter({
  space: (evt) => {
    simulateClick(evt);
  },
  enter: (evt) => {
    simulateClick(evt);
  },
});

const simulateClick = (evt) => {
  evt.stopPropagation();
  evt.preventDefault();
  evt.currentTarget.click();
};
